import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { doSignOut } from '../../firebase/auth';
import { onProjectChange, deleteProject } from '../../firebase/projects';
import { Users, Settings, LogOut, LayoutGrid, Folder, Film, Trash2, Menu, X } from 'lucide-react';
import Logo from '../common/Logo';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userLoggedIn, currentUser } = useAuth();
    
    const [project, setProject] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [activeTab, setActiveTab] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);


    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        if (pathParts[0] === 'projects' && pathParts[1]) {
            const currentProjectId = pathParts[1];
            const currentTab = pathParts[2] || '';
            setProjectId(currentProjectId);
            setActiveTab(currentTab);
        } else {
            setProjectId(null);
            setProject(null);
            setActiveTab('');
        }
    }, [location.pathname]);

    useEffect(() => {
        if (!projectId || !currentUser) {
            setProject(null);
            return;
        };

        const unsubscribe = onProjectChange(projectId, (projectData) => {
            if (projectData) {
                setProject(projectData);
            } else {
                setError('Project not found or you do not have access.');
                navigate('/projects');
            }
        });

        return () => unsubscribe();
    }, [projectId, currentUser, navigate]);

    const handleDeleteProject = async () => {
        if (!project || project.userId !== currentUser.uid) {
            setError('You do not have permission to delete this project.');
            return;
        }
        setIsDeleting(true);
        setError('');
        try {
            await deleteProject(projectId);
            navigate('/projects');
        } catch (err) {
            setError('Failed to delete project. Please try again.');
            console.error(err);
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };
    
    if (!userLoggedIn) {
        return null;
    }
    
    const isOwner = project?.userId === currentUser?.uid;
    const userPermissions = !isOwner && project?.permissions ? project.permissions[currentUser.uid] : null;

    const canAccessFiles = isOwner || userPermissions?.files;
    const canAccessMoodboard = isOwner || userPermissions?.moodboard;
    const canAccessSettings = isOwner;

    const ProjectNav = () => (
        <div className="flex items-center gap-x-4">
             <div 
                className="group relative flex items-center gap-x-2"
            >
                <span className="text-xl font-bold text-gray-800 dark:text-gray-200">{project.name}</span>
                {isOwner && (
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="p-1 rounded-md text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Delete project"
                    >
                        <Trash2 className="h-5 w-5" />
                    </button>
                )}
            </div>

            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700"></div>

            <nav className="flex space-x-2" aria-label="Tabs">
                {canAccessFiles && <TabButton name="files" />}
                {canAccessMoodboard && <TabButton name="moodboard" />}
                {canAccessSettings && <TabButton name="settings" />}
            </nav>
        </div>
    );
    
    const TabButton = ({ name }) => {
        const icons = {
            files: <Folder className="h-5 w-5" />,
            moodboard: <Film className="h-5 w-5" />,
            settings: <Settings className="h-5 w-5" />,
        };
        const isActive = activeTab === name || (name === 'files' && activeTab === '');

        return (
            <Link
                to={`/projects/${projectId}/${name}`}
                className={`${
                    isActive
                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                } flex items-center gap-2 whitespace-nowrap py-2 px-3 rounded-lg font-medium text-sm capitalize transition-colors`}
            >
                {icons[name]}
                <span className="hidden sm:inline">{name}</span>
            </Link>
        );
    }
    
    const mainNavLinks = (
        <>
            <Link to={'/projects'} className='flex items-center gap-x-2 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'>
                <LayoutGrid className='h-5 w-5' />
                <span className='text-sm font-medium'>Dashboard</span>
            </Link>
            {currentUser?.role && ['superadmin', 'admin', 'collaborator'].includes(currentUser.role) && (
                <Link to={'/users'} className='flex items-center gap-x-2 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'>
                    <Users className='h-5 w-5' />
                    <span className='text-sm font-medium'>Users</span>
                </Link>
            )}
            {currentUser?.role === 'superadmin' && (
                <Link to={'/settings'} className='flex items-center gap-x-2 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'>
                    <Settings className='h-5 w-5' />
                    <span className='text-sm font-medium'>Settings</span>
                </Link>
            )}
            <button onClick={() => { doSignOut().then(() => { navigate('/login') }) }} className='flex items-center gap-x-2 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors w-full text-left'>
                <LogOut className='h-5 w-5' />
                <span className='text-sm font-medium'>Logout</span>
            </button>
        </>
    );

    return (
        <>
            <nav className='flex flex-row items-center justify-between px-4 w-full z-20 fixed top-0 left-0 h-16 border-b bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm'>
                <div className='flex items-center gap-x-4'>
                    <Link className='flex-shrink-0' to={'/home'}>
                        <Logo />
                    </Link>
                </div>
                
                <div className="flex-1 flex justify-center">
                    {project && projectId && <ProjectNav />}
                </div>

                <div className='hidden md:flex items-center gap-x-1 sm:gap-x-2'>
                   {mainNavLinks}
                </div>
                <div className="md:hidden">
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-md text-gray-700 dark:text-gray-300">
                        {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </nav>
            {isMenuOpen && (
                <div className="md:hidden absolute top-16 right-4 w-auto bg-white dark:bg-gray-800 shadow-lg rounded-lg p-2 z-[70] border dark:border-gray-700">
                    <nav className="flex flex-col space-y-1">
                        {mainNavLinks}
                    </nav>
                </div>
            )}
            {showDeleteConfirm && project && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Are you sure you want to delete the project "{project.name}"? This action cannot be undone.</p>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                className="px-4 py-2 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 transition-colors disabled:opacity-50">
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteProject}
                                disabled={isDeleting}
                                className="px-4 py-2 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50">
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Header;
